import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import forEach from 'lodash/forEach';
import omit from 'lodash/omit';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Loader from 'yoda-core-components/lib/components/Loader/Loader';
import SlidePanel from 'yoda-account-components/lib/components/SlidePanel/SlidePanel';
import defaultWrapperRenderer from 'yoda-site-components/lib/components/IrisPage/defaultWrapperRenderer';
import renderCurrentComponent from 'yoda-site-components/lib/components/IrisPage/renderCurrentComponent';
import * as irisComponentTypes from 'yoda-site-components/lib/iris/components/common';
import {
    getCashpassExclusions,
    resetCashpassExclusions,
} from '../../actions/CashpassExclusionsAction';

/**
 * Flattens the content-aggregator response into a flat list of IRIS components.
 *
 * The aggregate-pages response can arrive in two shapes:
 *  1. A zone with components directly under content: `content.components[]`
 *  2. The IRIS page layout: `content.rows[].columns[].components[]`
 *
 * Each entry is a standard IRIS component (componentType + content) that the
 * shared IRIS renderer knows how to render, the same way yoda-marketing pages do.
 * Kept as a named export so it can be tested independently.
 */
export const getExclusionsComponents = (content) => {
    const directComponents = get(content, 'content.components', null);
    if (Array.isArray(directComponents) && directComponents.length > 0) {
        return directComponents;
    }

    const rows = get(content, 'content.rows', []) || [];
    return rows.reduce((components, row) => {
        const columns = get(row, 'columns', []) || [];
        columns.forEach((column) => {
            components.push(...(get(column, 'components', []) || []));
        });
        return components;
    }, []);
};

/**
 * Slide-panel version of the Cashpass "Exclusions & Details" link. Reads the
 * content from the `cashpassExclusions` store slice and dispatches the fetch on
 * first open. The saga caches the content, so re-opening the panel will not
 * trigger another network call.
 *
 * Content is rendered with the shared IRIS component renderer
 * (renderCurrentComponent + defaultWrapperRenderer) so it displays identically
 * to how the standalone /m/rewards-redemptions-exclusions marketing page renders.
 */
export const CashpassExclusionsSlidePanel = ({
    isOpen,
    onClose,
    content,
    showLoader,
    hasAttempted,
    error,
    fetchExclusions,
    resetExclusions,
    preferences,
    featureFlags,
    deviceType,
}) => {
    useEffect(() => {
        // Only fetch on first open and only if we have never attempted before.
        // hasAttempted covers the error case (isLoaded stays false on error),
        // so a failing API is not retried on every re-render.
        if (isOpen && !hasAttempted && !showLoader) {
            fetchExclusions();
        }
    }, [isOpen, hasAttempted, showLoader, fetchExclusions]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflowY = 'hidden';
        } else {
            document.body.style.overflowY = 'auto';
        }
        return () => {
            document.body.style.overflowY = 'auto';
        };
    }, [isOpen]);

    // On close, reset the attempt/error state so a failed fetch retries on the
    // next open. The reducer keeps successfully-cached content, so a successful
    // load is never re-fetched.
    const handleClose = () => {
        resetExclusions();
        onClose();
    };

    if (!isOpen) return null;

    const components = getExclusionsComponents(content);
    const hasContent = Array.isArray(components) && components.length > 0;

    // Render each IRIS component through the shared renderer, mirroring how
    // LaunchNLearn / yoda-marketing render aggregate-page content.
    const renderIrisComponents = () => {
        const colDom = [];
        forEach(components, (component, index) => {
            const item = defaultWrapperRenderer(
                renderCurrentComponent(
                    component,
                    index,
                    irisComponentTypes.default,
                    omit(irisComponentTypes.default, ['gridcomponent']),
                    { preferences, featureFlags, deviceType }
                )
            );
            colDom.push(item);
        });
        return colDom;
    };

    return createPortal(
        <SlidePanel
            isOpenPanel={isOpen}
            onClosePanel={handleClose}
            showBackIcon={false}
            onPrimaryCloseButtonClick={handleClose}
            isPrimaryCloseButtonRequired
            panelTitle="Exclusions & Details"
        >
            <div className={dt(['p-4'])} data-automation-id="cashpass-exclusions-content">
                {showLoader && <Loader keepOverlay automationId="cashpass-exclusions-loader" />}

                {!showLoader && error && (
                    <div
                        className={dt(['text-normal', 'text-black'])}
                        data-automation-id="cashpass-exclusions-error"
                    >
                        We&apos;re unable to load the details right now. Please try again later.
                    </div>
                )}

                {!showLoader && !error && hasContent && renderIrisComponents()}
            </div>
        </SlidePanel>,
        document.body
    );
};

CashpassExclusionsSlidePanel.propTypes = {
    /** Whether the slide panel is open. */
    isOpen: PropTypes.bool.isRequired,
    /** Called when the panel requests to close. */
    onClose: PropTypes.func.isRequired,
    /** Raw content payload from the content-aggregator response (store-cached). */
    content: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
    /** True while the content is being fetched. */
    showLoader: PropTypes.bool,
    /** True once a fetch has been attempted (success or error). Prevents retries. */
    hasAttempted: PropTypes.bool,
    /** Error from the fetch, if any. */
    error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    /** Dispatches the (cache-aware) content fetch. */
    fetchExclusions: PropTypes.func.isRequired,
    /** Clears attempt/error state on close so a failed fetch can retry on re-open. */
    resetExclusions: PropTypes.func.isRequired,
    /** App preferences passed to the IRIS component renderer. */
    preferences: PropTypes.objectOf(PropTypes.any),
    /** Feature flags passed to the IRIS component renderer. */
    featureFlags: PropTypes.objectOf(PropTypes.any),
    /** Device type passed to the IRIS component renderer. */
    deviceType: PropTypes.objectOf(PropTypes.any),
};

CashpassExclusionsSlidePanel.defaultProps = {
    content: null,
    showLoader: false,
    hasAttempted: false,
    error: null,
    preferences: {},
    featureFlags: {},
    deviceType: {},
};

const mapStateToProps = (state) => ({
    content: get(state, 'cashpassExclusions.content', null),
    showLoader: get(state, 'cashpassExclusions.showLoader', false),
    hasAttempted: get(state, 'cashpassExclusions.hasAttempted', false),
    error: get(state, 'cashpassExclusions.error', null),
    preferences: get(state, 'context.preferences', {}),
    featureFlags: get(state, 'context.featureFlags', {}),
    deviceType: get(state, 'context.deviceType', {}),
});

const mapDispatchToProps = {
    fetchExclusions: getCashpassExclusions,
    resetExclusions: resetCashpassExclusions,
};

export default connect(mapStateToProps, mapDispatchToProps)(CashpassExclusionsSlidePanel);
